import { observable } from "@legendapp/state";
import { getAudienceSegmentReport } from "@/apis/clients/reportingClient";
import {
  createRequestGuard,
  reportScopeKey,
  toErrorMessage,
} from "@/stores/storeHelpers";
import type { PerformanceReportRow, ReportScope } from "@/types/reporting";

interface AudienceSegmentState {
  rows: PerformanceReportRow[];
  isLoading: boolean;
  error: string | null;
  loadedForScopeKey: string | null;
}

const defaultValues: AudienceSegmentState = {
  rows: [],
  isLoading: false,
  error: null,
  loadedForScopeKey: null,
};

export const AudienceSegmentStore$ = observable(defaultValues);

const getScopeKey = (scope: ReportScope) =>
  `audience-segment:${reportScopeKey(scope)}`;

const guard = createRequestGuard();

export const fetchAudienceSegment = async (scope: ReportScope) => {
  const scopeKey = getScopeKey(scope);
  const result = guard.check(
    scopeKey,
    AudienceSegmentStore$.loadedForScopeKey.get()
  );
  if (result.outcome === "cached") {
    AudienceSegmentStore$.assign({ isLoading: false, error: null });
    return;
  }
  if (result.outcome === "in-flight") return;
  const { requestId } = result;
  AudienceSegmentStore$.assign({ isLoading: true, error: null });

  try {
    const response = await getAudienceSegmentReport(scope);
    if (guard.isStale(requestId)) return;
    guard.clearPending(scopeKey);
    AudienceSegmentStore$.assign({
      rows: response.results,
      loadedForScopeKey: scopeKey,
    });
  } catch (error) {
    if (guard.isStale(requestId)) return;
    guard.clearPending(scopeKey);
    AudienceSegmentStore$.error.set(toErrorMessage(error));
  } finally {
    if (!guard.isStale(requestId)) {
      AudienceSegmentStore$.isLoading.set(false);
    }
  }
};

export const clearAudienceSegment = () => {
  guard.invalidate();
  AudienceSegmentStore$.assign({
    error: null,
    isLoading: false,
    loadedForScopeKey: null,
    rows: [],
  });
};
